import React from 'react';
import styled from 'styled-components';

import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants/specials';
import { RibbonStarOutline } from 'src/assets/ribbon-star-outline';
import type { IrlEventRewardType } from 'src/gql/graphql';
import { useImage } from 'hooks/use-image';

type RewardCardProps = {
  rewardType: IrlEventRewardType | null;
  rewardName: string;
  rewardExpirationDate: string;
  rewardImageUrl: string;
};

export function RewardCard({
  rewardType,
  rewardName,
  rewardExpirationDate,
  rewardImageUrl,
}: RewardCardProps): JSX.Element {
  const { showImage, handleImageError } = useImage(rewardImageUrl);
  const isLoyaltyPointsBoost = rewardType === 'loyalty';

  return (
    <RewardContainer $isLoyalty={isLoyaltyPointsBoost} data-testid='irl-event-reward-card'>
      {!isLoyaltyPointsBoost && (
        <RewardImageContainer $showFallback={!showImage}>
          {showImage && <RewardImage src={rewardImageUrl} alt='' onError={handleImageError} />}
        </RewardImageContainer>
      )}
      {isLoyaltyPointsBoost && (
        <IconWrapper>
          <RibbonStarOutline />
        </IconWrapper>
      )}
      <RewardContent $isLoyalty={isLoyaltyPointsBoost}>
        <RewardName data-testid='irl-event-reward-card-name'>{rewardName}</RewardName>
        <RewardExpiration data-testid='irl-event-reward-card-expiration'>{rewardExpirationDate}</RewardExpiration>
      </RewardContent>
    </RewardContainer>
  );
}

const RewardContainer = styled.div<{ $isLoyalty: boolean }>`
  background: ${({ $isLoyalty }) => ($isLoyalty ? '#fff' : '#000')};
  background-position: center;
  background-size: cover;
  border: ${({ $isLoyalty }) => ($isLoyalty ? '1px solid #E3E7E9' : 'none')};
  border-radius: 20px;
  display: ${({ $isLoyalty }) => ($isLoyalty ? 'flex' : 'block')};
  align-items: ${({ $isLoyalty }) => ($isLoyalty ? 'center' : 'initial')};
  margin-bottom: 24px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  padding-left: 32px;
  display: flex;
  align-items: center;
`;

const RewardImageContainer = styled.div<{ $showFallback: boolean }>`
  background: ${({ $showFallback }) =>
    $showFallback
      ? `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 33.75%, rgba(0, 0, 0, 0.7) 100%), url(${DEFAULT_SPECIALS_CARD_IMG})`
      : 'none'};
  background-position: center;
  background-size: cover;
  position: absolute;
  inset: 0;
`;

const RewardImage = styled.img`
  display: block;
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
`;

const RewardContent = styled.div<{ $isLoyalty: boolean }>`
  color: ${({ $isLoyalty }) => ($isLoyalty ? '#000' : '#fff')};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px;
  padding-left: ${({ $isLoyalty }) => ($isLoyalty ? '20px' : '32px')};
  position: relative;
  text-align: left;
  z-index: 1;
`;

const RewardName = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
`;

const RewardExpiration = styled.p`
  font-size: 12px;
  line-height: 100%;
  margin: 0;
`;
